import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { User, TrendingUp, Clock, Heart, Brain, Sparkles, Eye, Zap, Star, ArrowRight } from 'lucide-react';
import { Agent } from '../lib/supabase';
import { AgentCard } from './AgentCard';

interface PersonalizedDashboardProps {
  onAgentSelect?: (agent: Agent) => void;
}

interface UserInsights {
  totalInteractions: number;
  favoriteCategories: string[];
  skillProgression: string;
  recommendedComplexity: string;
  recentActivity: any[];
  personalizedAgents: Agent[];
  trendingInYourInterests: Agent[];
}

export interface PersonalizedDashboardRef {
  loadUserInsights: () => Promise<void>;
}

export const PersonalizedDashboard = forwardRef<PersonalizedDashboardRef, PersonalizedDashboardProps>(({ onAgentSelect }, ref) => {
  const [insights, setInsights] = useState<UserInsights | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'recommendations' | 'activity' | 'favorites'>('recommendations');

  useEffect(() => {
    loadUserInsights();
  }, []);

  const loadUserInsights = async () => {
    setLoading(true);
    
    try {
      // In a real implementation, this would fetch from your analytics API
      // For now, we'll simulate personalized insights
      
      const mockInsights: UserInsights = {
        totalInteractions: 47,
        favoriteCategories: ['Chatbots', 'Content Creation', 'Data Analysis'],
        skillProgression: 'Intermediate → Advanced',
        recommendedComplexity: 'intermediate',
        recentActivity: [
          {
            type: 'analyzed',
            agent: 'CustomerBot Pro',
            timestamp: '2 hours ago',
            category: 'Chatbots'
          },
          {
            type: 'tested',
            agent: 'ContentGenie',
            timestamp: '1 day ago',
            category: 'Content Creation'
          },
          {
            type: 'built',
            agent: 'Custom Tourism Agent',
            timestamp: '2 days ago',
            category: 'Travel'
          }
        ],
        personalizedAgents: [
          {
            id: '1',
            name: 'Advanced Analytics Bot',
            description: 'Perfect for your data analysis needs with ML capabilities',
            source: 'github',
            source_url: 'https://github.com/example/analytics-bot',
            repository_url: 'https://github.com/example/analytics-bot',
            author: 'DataScience Corp',
            category: 'Data Analysis',
            tags: ['analytics', 'machine-learning', 'insights'],
            language: 'Python',
            framework: 'Scikit-learn',
            stars: 1850,
            downloads: 7200,
            last_updated: '2024-01-14',
            is_verified: true,
            metadata: { personalizedScore: 95 },
            created_at: '2024-01-01',
            updated_at: '2024-01-14'
          },
          {
            id: '2',
            name: 'Smart Content Assistant',
            description: 'AI writing assistant tailored to your content style',
            source: 'huggingface',
            source_url: 'https://huggingface.co/example/content-assistant',
            repository_url: 'https://huggingface.co/example/content-assistant',
            author: 'ContentAI',
            category: 'Content Creation',
            tags: ['writing', 'content', 'personalized'],
            language: 'Python',
            framework: 'Transformers',
            stars: 1200,
            downloads: 4500,
            last_updated: '2024-01-13',
            is_verified: true,
            metadata: { personalizedScore: 92 },
            created_at: '2024-01-01',
            updated_at: '2024-01-13'
          }
        ],
        trendingInYourInterests: [
          {
            id: '3',
            name: 'Multi-Modal Chatbot',
            description: 'Next-gen chatbot with image and voice capabilities',
            source: 'github',
            source_url: 'https://github.com/example/multimodal-bot',
            repository_url: 'https://github.com/example/multimodal-bot',
            author: 'AI Innovations',
            category: 'Chatbots',
            tags: ['multimodal', 'voice', 'image', 'chatbot'],
            language: 'Python',
            framework: 'LangChain',
            stars: 2100,
            downloads: 8900,
            last_updated: '2024-01-15',
            is_verified: true,
            metadata: { trendingScore: 98 },
            created_at: '2024-01-01',
            updated_at: '2024-01-15'
          }
        ]
      };

      setInsights(mockInsights);
    } catch (error) {
      console.error('Error loading user insights:', error);
    } finally {
      setLoading(false);
    }
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'analyzed': return <Brain className="w-4 h-4 text-blue-500" />;
      case 'tested': return <Zap className="w-4 h-4 text-green-500" />;
      case 'built': return <Sparkles className="w-4 h-4 text-purple-500" />;
      default: return <Eye className="w-4 h-4 text-gray-500" />;
    }
  };

  useImperativeHandle(ref, () => ({
    loadUserInsights: loadUserInsights,
  }));

  if (loading) {
    return (
      <div className="text-center py-12">
        <Brain className="w-12 h-12 text-blue-600 animate-pulse mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Loading Your Personalized Dashboard</h3>
        <p className="text-gray-600">Analyzing your preferences and activity...</p>
      </div>
    );
  }

  if (!insights) {
    return (
      <div className="text-center py-12">
        <User className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Welcome to Your Dashboard</h3>
        <p className="text-gray-600">Start interacting with agents to see personalized recommendations!</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <div className="flex items-center justify-center gap-3 mb-4">
          <div className="p-3 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl">
            <User className="w-8 h-8 text-white" />
          </div>
          <div>
            <h2 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Your AI Dashboard
            </h2>
            <p className="text-gray-600">Personalized recommendations based on your activity</p>
          </div>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="flex items-center gap-3">
            <TrendingUp className="w-8 h-8 text-blue-600" />
            <div>
              <p className="text-2xl font-bold text-gray-900">{insights.totalInteractions}</p>
              <p className="text-sm text-gray-600">Total Interactions</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="flex items-center gap-3">
            <Heart className="w-8 h-8 text-red-500" />
            <div>
              <p className="text-2xl font-bold text-gray-900">{insights.favoriteCategories.length}</p>
              <p className="text-sm text-gray-600">Favorite Categories</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="flex items-center gap-3">
            <Brain className="w-8 h-8 text-purple-600" />
            <div>
              <p className="text-lg font-bold text-gray-900 capitalize">{insights.recommendedComplexity}</p>
              <p className="text-sm text-gray-600">Skill Level</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="flex items-center gap-3">
            <Sparkles className="w-8 h-8 text-yellow-500" />
            <div>
              <p className="text-lg font-bold text-gray-900">{insights.skillProgression}</p>
              <p className="text-sm text-gray-600">Progression</p>
            </div>
          </div>
        </div>
      </div>

      {/* Favorite Categories */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-6 border border-blue-200">
        <h3 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
          <Heart className="w-5 h-5" />
          Your Favorite Categories
        </h3>
        <div className="flex flex-wrap gap-2">
          {insights.favoriteCategories.map((category, index) => (
            <span
              key={index}
              className="px-3 py-1 bg-blue-100 text-blue-700 rounded-lg text-sm font-medium"
            >
              {category}
            </span>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <div className="flex space-x-8">
          {[
            { id: 'recommendations', label: 'For You', icon: Sparkles },
            { id: 'activity', label: 'Recent Activity', icon: Clock },
            { id: 'favorites', label: 'Trending', icon: TrendingUp }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex items-center gap-2 py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'recommendations' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-semibold text-gray-900">Personalized for You</h3>
              <span className="text-sm text-gray-500">Based on your activity and preferences</span>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {insights.personalizedAgents.map((agent) => (
                <div key={agent.id} className="relative">
                  <AgentCard agent={agent} />
                  <div className="absolute top-4 right-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-2 py-1 rounded-lg text-xs font-medium">
                    {agent.metadata?.personalizedScore}% match
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
              <h4 className="font-semibold text-yellow-900 mb-2 flex items-center gap-2">
                <Sparkles className="w-5 h-5" />
                Why These Recommendations?
              </h4>
              <div className="text-yellow-800 text-sm space-y-1">
                <p>• Based on your favorite categories: {insights.favoriteCategories.join(', ')}</p>
                <p>• Matched to your {insights.recommendedComplexity} skill level</p>
                <p>• Similar to agents you've previously analyzed</p>
                <p>• Highly rated by users with similar interests</p>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'activity' && (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-gray-900">Recent Activity</h3>
            
            <div className="space-y-4">
              {insights.recentActivity.map((activity, index) => (
                <div key={index} className="bg-white border border-gray-200 rounded-lg p-4 flex items-center gap-4">
                  <div className="p-2 bg-gray-50 rounded-lg">
                    {getActivityIcon(activity.type)}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">
                      You {activity.type} <span className="text-blue-600">{activity.agent}</span>
                    </p>
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <span>{activity.timestamp}</span>
                      <span>•</span>
                      <span className="px-2 py-1 bg-gray-100 rounded text-xs">{activity.category}</span>
                    </div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-gray-400" />
                </div>
              ))}
            </div>

            <div className="text-center">
              <button className="px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                View All Activity
              </button>
            </div>
          </div>
        )}

        {activeTab === 'favorites' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-semibold text-gray-900">Trending in Your Interests</h3>
              <span className="text-sm text-gray-500">Popular among users like you</span>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {insights.trendingInYourInterests.map((agent) => (
                <div key={agent.id} className="relative">
                  <AgentCard agent={agent} />
                  <div className="absolute top-4 right-4 bg-gradient-to-r from-green-600 to-blue-600 text-white px-2 py-1 rounded-lg text-xs font-medium flex items-center gap-1">
                    <TrendingUp className="w-3 h-3" />
                    Trending
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-green-50 border border-green-200 rounded-xl p-6">
              <h4 className="font-semibold text-green-900 mb-2 flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                Trending Insights
              </h4>
              <div className="text-green-800 text-sm space-y-1">
                <p>• Multi-modal agents are gaining popularity in your favorite categories</p>
                <p>• 73% increase in chatbot interactions this week</p>
                <p>• Users like you are exploring more advanced complexity levels</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
});